import React, { useState } from 'react';
import {
  Activity,
  LogOut,
  ShieldCheck,
  Sparkles,
  Menu,
  X,
  Layers,
  Mail,
  Megaphone,
  BarChart3,
  CreditCard,
  Key,
  ShieldAlert,
  Gauge,
  FileText,
  Users,
  UserCheck,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { OrganizationSelector } from '../organization/OrganizationSelector';

export const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { canAccessRoute } = usePermissions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const allNavItems = [
    { to: '/', label: 'Overview', icon: Layers },
    { to: '/webmail', label: 'Webmail Client', icon: Mail },
    { to: '/campaigns', label: 'Email Campaigns', icon: Megaphone },
    { to: '/analytics', label: 'Email Analytics', icon: BarChart3 },
    { to: '/templates', label: 'Email Templates', icon: FileText },
    { to: '/contacts', label: 'Audience & Contacts', icon: Users },
    { to: '/deliverability', label: 'Deliverability & Warmup', icon: Gauge },
    { to: '/team', label: 'Team & Employees', icon: UserCheck },
    { to: '/billing', label: 'Billing & Purchases', icon: CreditCard },
    { to: '/api-keys', label: 'API Keys', icon: Key },
    { to: '/uam', label: 'Access Control (UAM)', icon: ShieldCheck },
    { to: '/health', label: 'Cluster Diagnostics', icon: Activity },
  ];

  if (user?.isSuperAdmin) {
    allNavItems.push({ to: '/admin', label: 'Platform Admin', icon: ShieldAlert });
  }

  const navItems = allNavItems.filter((item) => canAccessRoute(item.to));

  return (
    <>
      <header className="h-16 border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3 sm:gap-6">
          {/* Mobile Menu Hamburger Button */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800/80 transition"
            aria-label="Open Navigation Menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <Link to="/" className="flex items-center gap-2.5 font-bold text-base sm:text-lg text-white">
            <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 flex items-center justify-center shadow-lg shadow-brand-500/20 flex-shrink-0">
              <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
            </div>
            <span className="font-extrabold tracking-tight">keerav camp</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-950/80 text-brand-300 font-semibold border border-brand-800/80 hidden sm:inline tracking-wide">
              Axiino
            </span>
          </Link>

          {user && (
            <div className="hidden sm:block">
              <OrganizationSelector />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <Link
            to="/health"
            className="flex items-center gap-1.5 sm:gap-2 text-xs font-semibold px-2.5 sm:px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
          >
            <Activity className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">System Status</span>
            <span className="sm:hidden">Status</span>
          </Link>

          <div className="h-4 w-px bg-slate-800 hidden sm:block" />

          <div className="flex items-center gap-2 text-xs text-slate-400 hidden lg:flex">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Sovereign Mail Engine</span>
          </div>

          {user && (
            <div className="flex items-center gap-2 sm:gap-3 pl-2 border-l border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs text-brand-400 font-bold uppercase">
                  {user.firstName[0]}
                  {user.lastName[0]}
                </div>
                <div className="hidden md:flex flex-col text-left">
                  <span className="text-xs font-semibold text-white leading-tight">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono leading-tight">
                    {user.email}
                  </span>
                </div>
              </div>

              <button
                onClick={() => logout()}
                title="Sign Out"
                className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Drawer Backdrop & Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer Content */}
          <div className="relative w-72 max-w-[85vw] bg-slate-900 border-r border-slate-800 flex flex-col justify-between p-4 z-10 shadow-2xl animate-in slide-in-from-left duration-200">
            <div>
              <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
                <div className="flex items-center gap-2 font-bold text-white">
                  <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span>keerav camp</span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Organization Selector for Mobile */}
              {user && (
                <div className="mb-4 sm:hidden">
                  <OrganizationSelector />
                </div>
              )}

              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                Navigation
              </div>
              <nav className="space-y-1 overflow-y-auto max-h-[60vh] pr-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.to;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-600/15 text-brand-300 border border-brand-500/20'
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </nav>
            </div>

            <div className="pt-4 border-t border-slate-800 text-xs text-slate-400">
              <p className="font-semibold text-white">keerav camp</p>
              <p className="text-[11px] text-brand-400 font-medium">Developed by Axiino</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
